import { FileText, Film, FileSpreadsheet, FileArchive, File } from 'lucide-react'

interface FilePreviewProps {
    filename: string
    url: string
    mimetype?: string
    className?: string
}

export function isImageFile(filename: string) {
    return /\.(jpg|jpeg|png|webp|gif|svg|ico|bmp|tiff)$/i.test(filename)
}

export function isVideoFile(filename: string) {
    return /\.(mp4|webm|ogg|mov|avi|mkv)$/i.test(filename)
}

export function isPdfFile(filename: string) {
    return /\.pdf$/i.test(filename)
}

export function getFileIcon(filename: string) {
    if (isPdfFile(filename)) return FileText
    if (isVideoFile(filename)) return Film
    if (/\.(xlsx?|csv)$/i.test(filename)) return FileSpreadsheet
    if (/\.(zip|rar|7z|tar|gz)$/i.test(filename)) return FileArchive
    return File
}

export function getFileTypeLabel(filename: string) {
    if (isPdfFile(filename)) return 'PDF'
    if (isVideoFile(filename)) return 'Video'
    if (/\.(xlsx?|csv)$/i.test(filename)) return 'Tablo'
    if (/\.(zip|rar|7z|tar|gz)$/i.test(filename)) return 'Arşiv'
    const ext = filename.split('.').pop()?.toUpperCase()
    return ext || 'Dosya'
}

export function FilePreview({ filename, url, mimetype: _mimetype, className = '' }: FilePreviewProps) {
    if (isImageFile(filename)) {
        return (
            <img
                src={url}
                alt={filename}
                className={`w-full h-full object-cover ${className}`}
                loading="lazy"
            />
        )
    }

    if (isVideoFile(filename)) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center bg-muted/60 gap-2">
                <Film className="h-10 w-10 text-blue-500/70" />
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Video</span>
            </div>
        )
    }

    if (isPdfFile(filename)) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center bg-muted/60 gap-2">
                <FileText className="h-10 w-10 text-red-500/70" />
                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">PDF</span>
            </div>
        )
    }

    const Icon = getFileIcon(filename)
    const label = getFileTypeLabel(filename)

    return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-muted/60 gap-2">
            <Icon className="h-10 w-10 text-muted-foreground/50" />
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">{label}</span>
        </div>
    )
}

export function FilePreviewSidebar({ filename, url, mimetype: _mimetype }: FilePreviewProps) {
    if (isImageFile(filename)) {
        return <img src={url} alt={filename} className="w-full h-full object-contain" />
    }

    if (isVideoFile(filename)) {
        return (
            <video
                src={url}
                className="w-full h-full object-contain"
                controls
                preload="metadata"
            />
        )
    }

    if (isPdfFile(filename)) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                <FileText className="h-12 w-12 text-red-500/70" />
                <a href={url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                    PDF'i Görüntüle ↗
                </a>
            </div>
        )
    }

    const Icon = getFileIcon(filename)
    return (
        <div className="w-full h-full flex flex-col items-center justify-center gap-3">
            <Icon className="h-12 w-12 text-muted-foreground/40" />
            <span className="text-xs text-muted-foreground">{getFileTypeLabel(filename)}</span>
        </div>
    )
}
